import Phaser from 'phaser';
import { gameConfig, entityData, waves } from './gameData';
import { SPRITES } from './assetManifest';

// New: List of bot player data with increasing difficulty
const botPlayersData = [
  {
    name: 'bot 1',
    team: [
      { heroType: 'crocodile', level: 1 }
    ]
  },
  {
    name: 'bot 2',
    team: [
      { heroType: 'crocodile', level: 2 },
      { heroType: 'penguin', level: 1 }
    ]
  },
  {
    name: 'bot 3',
    team: [
      { heroType: 'crocodile', level: 3 },
      { heroType: 'penguin', level: 2 }
    ]
  }
  // Add more bot configurations as needed
];

class RescueRobotron extends Phaser.Scene {
  constructor() {
    super('RescueRobotron');
    this.gamePhase = null;
    // Updated player data structure
    this.playerData = {
      name: 'player 1',
      lastBattleResult: null,
      wins: 0,
      losses: 0,
      round: 1,
      gold: 2,
      team: [
        { heroType: 'penguin', level: 2 },
        { heroType: 'crocodile', level: 1 }
      ]
    }
    this.enemiesDefeated = 0;
    this.currentWave = 0;
    this.waveMultiplier = 1;
    this.isBattleOver = true;
  }

  preload() {
    Object.entries(SPRITES).forEach(([key, sprite]) => {
      this.load.image(key, sprite.path);
    });
    // Add this line to load the background image
    this.load.image('background', 'public/assets/background.png');
  }

  create() {
    const width = window.innerWidth;
    const height = window.innerHeight;

    // Add a full-screen background that scales dynamically
    this.add.image(width / 2, height / 2, 'background').setDisplaySize(width, height);

    this.redHeroes = this.physics.add.group();
    this.blueHeroes = this.physics.add.group();
    this.enemies = this.physics.add.group();
    this.humans = this.physics.add.group();
    this.heroBullets = this.physics.add.group();
    this.enemyBullets = this.physics.add.group();

    this.physics.add.overlap(this.heroBullets, this.enemies, this.bulletHitEnemy, null, this);
    this.physics.add.overlap([this.redHeroes, this.blueHeroes], this.humans, this.heroRescueHuman, null, this);
    this.physics.add.overlap(this.enemies, this.humans, this.enemyKillHuman, null, this);
    this.physics.add.overlap(this.enemies, [this.redHeroes, this.blueHeroes], this.enemyHitHero, null, this);
    this.physics.add.overlap(this.enemyBullets, [this.redHeroes, this.blueHeroes], this.enemyBulletHitHero, null, this);

    this.enterShopPhase();
  }

  enterBattlePhase() {
    this.gamePhase = 'battle';
    this.clearEntities();
    this.loadPlayerTeams();
    for (let i = 0; i < gameConfig.humanCount; i++) {
      this.createHuman();
    }
    this.enemiesDefeated = 0;
    this.currentWave = 0;
    this.waveMultiplier = 1;

    this.startNextWave();
    this.isBattleOver = false;
    this.setupHeroShooting();
  }

  enterShopPhase() {
    this.gamePhase = 'shop';
    this.clearEntities();
  }

  clearEnemies() {
    [this.enemies, this.enemyBullets].forEach(group => {
      const children = group.getChildren();
      for (let i = children.length - 1; i >= 0; i--) {
        this.destroyEntity(children[i]);
      }
      group.clear(true, true);
    });
  }

  destroyEntity(entity) {
    console.log(entity);
    if (entity.healthBar) entity.healthBar.destroy();
    if (entity.manaBar) entity.manaBar.destroy();
    if (entity.teamCircle) entity.teamCircle.destroy();
    entity.destroy();
  }

  clearEntities() {
    [this.redHeroes, this.blueHeroes, this.enemies, this.humans, this.heroBullets, this.enemyBullets].forEach(group => {
      const children = group.getChildren();
      for (let i = children.length - 1; i >= 0; i--) {
        this.destroyEntity(children[i]);
      }
      group.clear(true, true);
    });

    this.children.list
      .filter(child => child instanceof Phaser.GameObjects.Particles.ParticleEmitterManager)
      .forEach(particleManager => particleManager.destroy());
  }

  loadPlayerTeams() {
    // Load player team
    this.playerData.team.forEach(hero => {
      this.createHero(hero.heroType, 'red', hero.level);
    });

    // Load bot team based on player's round
    const botIndex = Math.min(this.playerData.round - 1, botPlayersData.length - 1);
    const bot = botPlayersData[botIndex];
    bot.team.forEach(hero => {
      this.createHero(hero.heroType, 'blue', hero.level);
    });
  }

  setupHeroShooting() {
    this.redHeroes.getChildren().concat(this.blueHeroes.getChildren()).forEach(hero => {
      this.time.addEvent({
        delay: 1000 / entityData[hero.type].shootFrequency[hero.level - 1],
        callback: () => this.shootBullet(hero),
        callbackScope: this,
        loop: true
      });
    });
  }

  update() {
    if (this.gamePhase === 'battle') {
      this.moveHeroes();
      this.moveEnemies();
      this.constrainEntities();
      this.destroyOffscreenBullets();
      this.maintainWaveComposition();
    }
  }

  constrainEntities() {
    const width = window.innerWidth;
    const height = window.innerHeight;

    this.redHeroes.getChildren().concat(this.blueHeroes.getChildren()).forEach(hero => {
      hero.x = Phaser.Math.Clamp(hero.x, 0, width);
      hero.y = Phaser.Math.Clamp(hero.y, 0, height);
    });
    this.enemies.getChildren().forEach(enemy => {
      enemy.x = Phaser.Math.Clamp(enemy.x, 0, width);
      enemy.y = Phaser.Math.Clamp(enemy.y, 0, height);
    });
    this.humans.getChildren().forEach(human => {
      human.x = Phaser.Math.Clamp(human.x, 0, width);
      human.y = Phaser.Math.Clamp(human.y, 0, height);
    });
  }

  destroyOffscreenBullets() {
    this.heroBullets.getChildren().forEach(bullet => {
      if (bullet.x < 0 || bullet.x > window.innerWidth || bullet.y < 0 || bullet.y > window.innerHeight) {
        bullet.destroy();
      }
    });
    this.enemyBullets.getChildren().forEach(bullet => {
      if (bullet.x < 0 || bullet.x > window.innerWidth || bullet.y < 0 || bullet.y > window.innerHeight) {
        bullet.destroy();
      }
    });
  }

  createHero(type, team, level) {
    const width = window.innerWidth;
    const height = window.innerHeight;

    const heroGroup = team === 'red' ? this.redHeroes : this.blueHeroes;
    const hero = heroGroup.create(Phaser.Math.Between(0, width), Phaser.Math.Between(0, height), entityData[type].image);

    hero.setCircle(10);
    hero.health = entityData[type].health[level - 1];
    hero.mana = 0;
    hero.invincible = false;
    hero.ultimateActive = false;
    hero.type = type;
    hero.team = team;
    hero.level = level;

    // Add the circle behind the hero sprite
    const circleRadius = 20;
    const circleColor = team === 'red' ? 0xFF3434 : 0x345DFF; // Red for hero, blue for enemy
    const teamCircle = this.add.circle(hero.x, hero.y, circleRadius, circleColor, 0.5); // 50% opacity

    // Ensure the circle follows the hero's position
    hero.teamCircle = teamCircle;

    // Set depth to make sure circle is behind the hero sprite
    hero.setDepth(1);  // Ensure hero sprite has a higher depth
    teamCircle.setDepth(0);  // Set the circle to be behind the sprite

    // Create health and mana bars
    hero.healthBar = this.add.rectangle(hero.x, hero.y - 20, 30, 5, 0x00ff00);
    hero.healthBar.setOrigin(0.5, 0.5);

    hero.manaBar = this.add.rectangle(hero.x, hero.y - 25, 30, 3, 0x0000ff);
    hero.manaBar.setOrigin(0.5, 0.5);

    // Update the circle's position in the update loop
    this.time.addEvent({
      delay: 16, // ~60 frames per second
      callback: () => {
        teamCircle.x = hero.x;
        teamCircle.y = hero.y;
      },
      loop: true
    });
  }

  createEnemy(type) {
    let x, y;
    const side = Phaser.Math.Between(0, 3);
    const width = window.innerWidth;
    const height = window.innerHeight;

    switch (side) {
      case 0:
        x = Phaser.Math.Between(0, width);
        y = -10;
        break;
      case 1:
        x = width + 10;
        y = Phaser.Math.Between(0, height);
        break;
      case 2:
        x = Phaser.Math.Between(0, width);
        y = height + 10;
        break;
      case 3:
        x = -10;
        y = Phaser.Math.Between(0, height);
        break;
    }

    const enemy = this.enemies.create(x, y, entityData[type].image);
    enemy.setCircle(10);
    enemy.health = entityData[type].health;
    enemy.type = type;

    enemy.healthBar = this.add.rectangle(enemy.x, enemy.y - 20, 30, 5, 0xff0000);
    enemy.healthBar.setOrigin(0.5, 0.5);

    if (type === 'shooterEnemy') {
      this.time.addEvent({
        delay: entityData.shooterEnemy.shootInterval,
        callback: () => this.enemyShootBullet(enemy),
        callbackScope: this,
        loop: true
      });
    }
  }

  createHuman() {
    const human = this.humans.create(Phaser.Math.Between(0, window.innerWidth), Phaser.Math.Between(0, window.innerHeight), entityData.human.image);
    human.setCircle(7.5);
  }

  moveHeroes() {
    const heroes = this.redHeroes.getChildren().concat(this.blueHeroes.getChildren());
    heroes.forEach(hero => {
      let moveVector = new Phaser.Math.Vector2(0, 0);

      this.enemyBullets.getChildren().forEach(bullet => {
        const distance = Phaser.Math.Distance.Between(hero.x, hero.y, bullet.x, bullet.y);
        if (distance < 100) {
          let avoidVector = new Phaser.Math.Vector2(hero.x - bullet.x, hero.y - bullet.y);
          avoidVector.normalize();
          avoidVector.scale(1 / distance * 50000);
          moveVector.add(avoidVector);
        }
      });

      this.enemies.getChildren().forEach(enemy => {
        const distance = Phaser.Math.Distance.Between(hero.x, hero.y, enemy.x, enemy.y);
        if (distance < 150) {
          let avoidVector = new Phaser.Math.Vector2(hero.x - enemy.x, hero.y - enemy.y);
          avoidVector.normalize();
          avoidVector.scale(1 / distance * 20000);
          moveVector.add(avoidVector);
        }
      });

      heroes.forEach(otherHero => {
        if (hero !== otherHero) {
          const distance = Phaser.Math.Distance.Between(hero.x, hero.y, otherHero.x, otherHero.y);
          if (distance < 50) {
            let separationVector = new Phaser.Math.Vector2(hero.x - otherHero.x, hero.y - otherHero.y);
            separationVector.normalize();
            separationVector.scale(1 / distance * 500);
            moveVector.add(separationVector);
          }
        }
      });

      const otherTeamHeroes = hero.team === 'red' ? this.blueHeroes.getChildren() : this.redHeroes.getChildren();
      otherTeamHeroes.forEach(otherHero => {
        const distance = Phaser.Math.Distance.Between(hero.x, hero.y, otherHero.x, otherHero.y);
        let avoidVector = new Phaser.Math.Vector2(hero.x - otherHero.x, hero.y - otherHero.y);
        avoidVector.normalize();
        avoidVector.scale(1 / distance * 10000);
        moveVector.add(avoidVector);
      });

      const nearestHuman = this.findNearest(hero, this.humans.getChildren());
      if (nearestHuman) {
        let humanVector = new Phaser.Math.Vector2(nearestHuman.x - hero.x, nearestHuman.y - hero.y);
        humanVector.normalize();
        humanVector.scale(300);
        moveVector.add(humanVector);
      }

      let centerVector = new Phaser.Math.Vector2(window.innerWidth / 2 - hero.x, window.innerHeight / 2 - hero.y);
      centerVector.normalize();
      centerVector.scale(100);
      moveVector.add(centerVector);

      if (moveVector.length() > 0) {
        moveVector.normalize();
        moveVector.scale(entityData[hero.type].speed);
      }

      hero.setVelocity(moveVector.x, moveVector.y);

      hero.healthBar.x = hero.x;
      hero.healthBar.y = hero.y - 20;
      hero.healthBar.width = (hero.health / entityData[hero.type].health[hero.level - 1]) * 30;

      hero.manaBar.x = hero.x;
      hero.manaBar.y = hero.y - 25;
      hero.manaBar.width = (hero.mana / entityData[hero.type].manaMax) * 30;

      hero.teamCircle.x = hero.x;
      hero.teamCircle.y = hero.y;

      if (hero.mana >= entityData[hero.type].manaMax && !hero.ultimateActive) {
        this.activateUltimate(hero);
      }
    });
  }

  moveEnemies() {
    this.enemies.getChildren().forEach(enemy => {
      const nearestHero = this.findNearest(enemy, this.redHeroes.getChildren().concat(this.blueHeroes.getChildren()));
      if (nearestHero) {
        this.physics.moveToObject(enemy, nearestHero, entityData[enemy.type].speed);
      }
      enemy.healthBar.x = enemy.x;
      enemy.healthBar.y = enemy.y - 20;
    });
  }

  shootBullet(hero) {
    if (hero.active) {
      const nearestEnemy = this.findNearest(hero, this.enemies.getChildren());
      let angle = nearestEnemy
        ? Phaser.Math.Angle.Between(hero.x, hero.y, nearestEnemy.x, nearestEnemy.y)
        : Phaser.Math.DegToRad(Phaser.Math.Between(0, 360));

      const scatterCount = hero.ultimateActive && hero.type === 'penguin' ? 10 : entityData.penguin.scatterCount[hero.level - 1];

      if (hero.type === 'penguin') {
        for (let i = 0; i < scatterCount; i++) {
          const scatterAngle = angle + Phaser.Math.DegToRad(Phaser.Math.Between(-30, 30));
          const bullet = this.heroBullets.create(hero.x, hero.y, entityData[hero.type].bulletImage);
          bullet.setRotation(scatterAngle);
          this.physics.velocityFromRotation(scatterAngle, entityData[hero.type].bulletSpeed, bullet.body.velocity);
          bullet.damage = entityData[hero.type].bulletDamage[hero.level - 1];
        }
      } else {
        const bullet = this.heroBullets.create(hero.x, hero.y, entityData[hero.type].bulletImage);
        bullet.setRotation(angle);
        this.physics.velocityFromRotation(angle, entityData[hero.type].bulletSpeed, bullet.body.velocity);
        bullet.damage = entityData[hero.type].bulletDamage[hero.level - 1];
      }
    }
  }

  enemyShootBullet(enemy) {
    const nearestHero = this.findNearest(enemy, this.redHeroes.getChildren().concat(this.blueHeroes.getChildren()));
    if (nearestHero) {
      let angle = Phaser.Math.Angle.Between(enemy.x, enemy.y, nearestHero.x, nearestHero.y);
      const bullet = this.enemyBullets.create(enemy.x, enemy.y, entityData.shooterEnemy.bulletImage);
      bullet.setRotation(angle);
      this.physics.velocityFromRotation(angle, entityData.shooterEnemy.bulletSpeed, bullet.body.velocity);
    }
  }

  findNearest(source, targets) {
    return targets.reduce((nearest, target) => {
      const distance = Phaser.Math.Distance.Between(source.x, source.y, target.x, target.y);
      return (!nearest || distance < nearest.distance) ? { distance, target } : nearest;
    }, null)?.target;
  }

  bulletHitEnemy(bullet, enemy) {
    bullet.destroy();
    enemy.health -= bullet.damage;
    enemy.healthBar.width = (enemy.health / entityData[enemy.type].health) * 30;

    this.tweens.add({
      targets: enemy,
      tint: 0xffffff,
      duration: 100,
      yoyo: true,
      onComplete: () => {
        enemy.clearTint();
      }
    });

    if (enemy.health <= 0) {
      enemy.healthBar.destroy();
      this.createExplosion(enemy.x, enemy.y, 'small');
      enemy.destroy();
      this.enemiesDefeated++;
      this.checkWaveCompletion();
    }
  }

  heroRescueHuman(hero, human) {
    human.destroy();
    hero.mana = Math.min(hero.mana + 20, entityData[hero.type].manaMax);
    if (this.humans.countActive() < gameConfig.humanCount) {
      this.createHuman();
    }
  }

  enemyKillHuman(enemy, human) {
    human.destroy();
    if (this.humans.countActive() < gameConfig.humanCount) {
      this.createHuman();
    }
  }

  enemyHitHero(enemy, hero) {
    if (!hero.invincible) {
      hero.health--;
      hero.healthBar.width = (hero.health / entityData[hero.type].health[hero.level - 1]) * 30;
      hero.invincible = true;
      hero.alpha = 0.5;

      this.time.delayedCall(1000, () => {
        hero.invincible = false;
        hero.alpha = 1;
      });

      if (hero.health <= 0) {
        this.createMassiveExplosion(hero.x, hero.y);
        this.cameras.main.shake(500, 0.05);
        hero.healthBar.destroy();
        hero.manaBar.destroy();
        hero.teamCircle.destroy();
        hero.destroy();
        this.checkGameOver();
      }
    }
  }

  enemyBulletHitHero(bullet, hero) {
    if (!hero.invincible) {
      bullet.destroy();
      hero.health--;
      hero.healthBar.width = (hero.health / entityData[hero.type].health[hero.level - 1]) * 30;
      hero.invincible = true;
      hero.alpha = 0.5;

      this.time.delayedCall(1000, () => {
        hero.invincible = false;
        hero.alpha = 1;
      });

      if (hero.health <= 0) {
        this.createMassiveExplosion(hero.x, hero.y);
        this.cameras.main.shake(500, 0.05);
        hero.healthBar.destroy();
        hero.manaBar.destroy();
        hero.teamCircle.destroy();
        hero.destroy();
        this.checkGameOver();
      }
    }
  }

  createMassiveExplosion(x, y) {
    const particleCount = 500;
    const particleSpeed = 1000;

    const particles = this.add.particles('massiveExplosionParticle');
    const emitter = particles.createEmitter({
      x: x,
      y: y,
      speed: { min: -particleSpeed, max: particleSpeed },
      angle: { min: 0, max: 360 },
      scale: { start: 2, end: 0 },
      blendMode: 'NORMAL',
      lifespan: 1000,
      gravityY: 0
    });
    emitter.explode(particleCount);

    const flash = this.add.rectangle(0, 0, window.innerWidth, window.innerHeight, 0xffffff);
    flash.setOrigin(0);
    flash.setAlpha(1);

    this.tweens.add({
      targets: flash,
      alpha: 0,
      duration: 1000,
      ease: 'Power2',
      onComplete: () => {
        flash.destroy();
      }
    });

    const ring = this.add.circle(x, y, 10, 0xffffff);
    ring.setAlpha(0.5);

    this.tweens.add({
      targets: ring,
      radius: Math.max(window.innerWidth, window.innerHeight),
      alpha: 0,
      duration: 1000,
      ease: 'Quad.easeOut',
      onComplete: () => {
        ring.destroy();
      }
    });
  }

  createExplosion(x, y, size) {
    const particleCount = size === 'large' ? 50 : 20;
    const particleSpeed = size === 'large' ? 200 : 100;

    const particles = this.add.particles('explosionParticle');
    const emitter = particles.createEmitter({
      x: x,
      y: y,
      speed: { min: -particleSpeed, max: particleSpeed },
      angle: { min: 0, max: 360 },
      scale: { start: 0.5, end: 0 },
      blendMode: 'NORMAL',
      lifespan: 500,
      gravityY: 0
    });

    emitter.explode(particleCount);
  }

  activateUltimate(hero) {
    hero.ultimateActive = true;
    hero.mana = 0;
    hero.setTint(0xffff00);

    const particles = this.add.particles('ultimateParticle');
    const emitter = particles.createEmitter({
      follow: hero,
      scale: { start: 0.5, end: 0 },
      blendMode: 'ADD',
      lifespan: 200,
      frequency: 50
    });

    if (entityData[hero.type].ultimateType === 'stim') {
      this.time.addEvent({
        delay: 1000 / (entityData[hero.type].shootFrequency[hero.level - 1] * 2),
        callback: () => this.shootBullet(hero),
        callbackScope: this,
        repeat: entityData[hero.type].ultimateDuration / (1000 / (entityData[hero.type].shootFrequency[hero.level - 1] * 2))
      });
    } else if (entityData[hero.type].ultimateType === 'invincibility') {
      hero.invincible = true;
    }

    this.time.delayedCall(entityData[hero.type].ultimateDuration, () => {
      hero.ultimateActive = false;
      hero.clearTint();
      emitter.stop();
      if (entityData[hero.type].ultimateType === 'invincibility') {
        hero.invincible = false;
      }
    });
  }

  maintainWaveComposition() {
    if (this.isBattleOver) {
      return;
    }

    const waveData = waves[this.currentWave];
    const totalTargetEnemies = Object.entries(waveData)
      .filter(([key]) => key !== 'killCount')
      .reduce((sum, [, count]) => sum + count, 0);

    Object.entries(waveData).forEach(([enemyType, targetCount]) => {
      if (enemyType !== 'killCount') {
        const currentCount = this.enemies.getChildren().filter(enemy => enemy.type === enemyType).length;
        const difference = targetCount * this.waveMultiplier - currentCount;

        for (let i = 0; i < difference; i++) {
          this.createEnemy(enemyType);
        }
      }
    });
  }

  checkWaveCompletion() {
    if (this.enemiesDefeated >= waves[this.currentWave].killCount * this.waveMultiplier) {
      this.currentWave++;
      this.startNextWave();
    }
  }

  startNextWave() {
    if (this.currentWave >= waves.length) {
      this.currentWave = 0;
      this.waveMultiplier *= 10;
    }

    this.enemiesDefeated = 0;
  }

  checkGameOver() {
    if (this.redHeroes.countActive() === 0 || this.blueHeroes.countActive() === 0) {
      const winner = this.redHeroes.countActive() > 0 ? 'Red' : 'Blue';

      if (winner === 'Red') {
        this.playerData.wins++;
        this.playerData.round++;
      } else {
        this.playerData.losses++;
      }
      this.playerData.gold += this.playerData.round;

      this.playerData.lastBattleResult = winner === 'Red' ? 'win' : 'lose';

      this.isBattleOver = true;
      this.time.removeAllEvents();

      this.clearEnemies();

      this.time.delayedCall(3000, () => {
        this.enterShopPhase();
      });
    }
  }
}

export default RescueRobotron;
